import { useState, useEffect } from 'react';
import {
    Card, CardHeader, CardContent, Box, Button, Typography,
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import axios from '../utils/axios';

const PIN_LENGTH = 6;

function formatPinDisplay(value) {
    const s = String(value).padEnd(PIN_LENGTH, ' ').slice(0, PIN_LENGTH);
    return `${s.slice(0, 2)}-${s.slice(2, 4)}-${s.slice(4, 6)}`.replace(/\s/g, '_');
}

export default function TicketByPin({ showPopup, setInProgress }) {
    const [tournaments, setTournaments] = useState([]);
    const [loadingTournaments, setLoadingTournaments] = useState(true);
    const [selectedTournamentId, setSelectedTournamentId] = useState('');
    const [pin, setPin] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const payload = {
            user: typeof window !== 'undefined' && window.Telegram?.WebApp?.initDataUnsafe?.user,
            initData: typeof window !== 'undefined' && window.Telegram?.WebApp?.initData,
        };
        axios.post('/api/ticket/tournaments', payload)
            .then((res) => {
                const data = res.data?.data ?? res.data;
                const list = Array.isArray(data) ? data : (data?.data ?? []);
                setTournaments(list);
                if (list.length > 0 && !selectedTournamentId) {
                    setSelectedTournamentId(String(list[0].id));
                }
            })
            .catch(() => setTournaments([]))
            .finally(() => setLoadingTournaments(false));
    }, []);

    const handleDigit = (d) => {
        setPin((prev) => {
            const next = prev + String(d);
            return next.length <= PIN_LENGTH ? next : prev;
        });
    };

    const handleBackspace = () => {
        setPin((prev) => prev.slice(0, -1));
    };

    const handleClear = () => setPin('');

    const handleSubmit = () => {
        const id_tournament = selectedTournamentId ? Number(selectedTournamentId) : 0;
        const pinNum = Number(pin);
        if (!id_tournament || pin.length !== PIN_LENGTH || pinNum < 100000) {
            if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
                window.Telegram.WebApp.showAlert('Оберіть подію та введіть 6-значний PIN');
            }
            return;
        }
        setSubmitting(true);
        const payload = {
            id_tournament,
            pin: pinNum,
            user: window.Telegram?.WebApp?.initDataUnsafe?.user,
            initData: window.Telegram?.WebApp?.initData,
        };
        axios.post('/api/ticket/check-pin', payload)
            .then((res) => {
                const data = res.data;
                showPopup(data);
                setPin('');
            })
            .catch((err) => {
                const msg = err?.response?.data?.message ?? err?.message ?? 'Помилка';
                if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
                    window.Telegram.WebApp.showAlert(msg);
                }
            })
            .finally(() => {
                setSubmitting(false);
                setInProgress?.(false);
            });
        setInProgress?.(true);
    };

    return (
        <Card>
            <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Подія</InputLabel>
                        <Select
                            value={selectedTournamentId}
                            label="Подія"
                            onChange={(e) => setSelectedTournamentId(e.target.value)}
                            disabled={loadingTournaments}
                        >
                            {tournaments.map((t) => (
                                <MenuItem key={t.id} value={String(t.id)}>
                                    {t.name} {t.date_formatted ?? ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Typography variant="h5" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 600 }}>
                        {formatPinDisplay(pin)}
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 1.5,
                            '& .MuiButton-root': { minHeight: 52, fontSize: '1.1rem' },
                        }}
                    >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'back'].map((d) => {
                            if (d === null) return <Box key="empty" />;
                            if (d === 'back') {
                                return (
                                    <Button key="back" variant="contained" onClick={handleBackspace} disabled={!pin.length}>
                                        ⌫
                                    </Button>
                                );
                            }
                            return (
                                <Button key={d} variant="contained" onClick={() => handleDigit(d)}>
                                    {d}
                                </Button>
                            );
                        })}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" onClick={handleClear} fullWidth disabled={!pin.length}>
                            Очистити
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            fullWidth
                            disabled={pin.length !== PIN_LENGTH || !selectedTournamentId || submitting}
                        >
                            Сканувати
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}
